import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { addCashierBoxMovementV2, createCashierBoxV2, deleteCashierBoxByIdV2, deleteCashierBoxMovementById, updateCashierBoxByIdV2 } from '../../services/store.service';
import type { IInOutMovement } from '../../interfaces/Store';

export class CreatedCashierBoxMovementContractError extends Error {
  constructor() {
    super('Created cashier box movement response does not match the expected contract.');
    this.name = 'CreatedCashierBoxMovementContractError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSerializableId(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return Number.isFinite(value);

  if (isRecord(value)) {
    const stringValue = String(value).trim();
    return stringValue.length > 0 && stringValue !== '[object Object]';
  }

  return false;
}

function isValidDateInput(value: unknown): boolean {
  if (!(typeof value === 'string' || typeof value === 'number' || value instanceof Date)) {
    return false;
  }

  return !Number.isNaN(new Date(value).getTime());
}

function isValidCreatedBy(value: unknown): boolean {
  return typeof value === 'string' || isRecord(value);
}

export function isCreatedCashierBoxMovement(value: unknown): value is IInOutMovement {
  if (!isRecord(value)) return false;

  return (
    isSerializableId(value._id) &&
    (value.type === 'in' || value.type === 'out') &&
    isValidDateInput(value.date) &&
    typeof value.amount === 'number' &&
    Number.isFinite(value.amount) &&
    typeof value.paymentMethod === 'string' &&
    value.paymentMethod.trim().length > 0 &&
    typeof value.notes === 'string' &&
    isValidCreatedBy(value.createdBy)
  );
}

function getResponseType(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

export const useDeleteCashierBoxById = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const res = await deleteCashierBoxByIdV2(storeId, id);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['cashierbox', { storeId: storeId }],
      })]);
    }
  });
};

export const useCreateCashierBox = (storeId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      try {
        const res = await createCashierBoxV2(storeId, data);
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while creating.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async () => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['cashierbox', { storeId: storeId }],
      })]);
    }
  });
};

export const useUpdateCashierboxId = (storeId: string, id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { name: string }) => {
      try {
        const res = await updateCashierBoxByIdV2(storeId, id, data);
        if (!res?.data) {
          throw new Error('Oh no! Something went wrong while updating.');
        }
        return res.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async () => {
      //INVALIDATE CACHED  WITH STORE ID AND ID
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['cashierbox', { storeId: storeId }],
      }), queryClient.invalidateQueries({
        queryKey: ['cashierbox', { _id: id }],
      })]);
    }
  });
};

export const useAddCashierBoxMovementV2 = (storeId: string, id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      type: string;
      date: Date;
      amount: number,
      notes?: string;
      paymentMethod: string;
    }) => {
      try {
        const res = await addCashierBoxMovementV2(storeId, id, data);
        const createdMovement = res?.data?.data;

        if (!isCreatedCashierBoxMovement(createdMovement)) {
          console.error('[CashierBoxMovement] Invalid created movement response', {
            storeId,
            cashierBoxId: id,
            responseType: getResponseType(createdMovement),
          });
          throw new CreatedCashierBoxMovementContractError();
        }

        return createdMovement;
      } catch (e: any) {
        if (!(e instanceof CreatedCashierBoxMovementContractError)) {
          console.log('error caught', e['response']?.data?.['errors']);
        }
        throw e;
      }
    },
    onSuccess: async () => {
      //INVALIDATE CACHED  WITH STORE ID AND ID
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['cashierbox', { storeId: storeId }],
      }), queryClient.invalidateQueries({
        queryKey: ['cashierbox', { _id: id }],
      }), queryClient.invalidateQueries({
        queryKey: ['cashierboxHistory', { _id: id }],
      })]);
    }
  });
};

export const useDeleteCashierBoxMovementById = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      storeId, cashierBoxId, movementId
    }: {
      storeId: string,
      cashierBoxId: string,
      movementId: string
    }) => {
      try {
        const res = await deleteCashierBoxMovementById({ storeId, cashierBoxId, movementId });
        if (!res?.data?.data) {
          throw new Error('Oh no! Something went wrong while fetching.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e['response']?.data?.['errors']);
        throw e;
      }
    },
    onSuccess: async ({ storeId, cashierBoxId }) => {
      return Promise.all([queryClient.invalidateQueries({
        queryKey: ['cashierbox', { storeId: storeId, _id: cashierBoxId }],
      }), queryClient.invalidateQueries({
        queryKey: ['cashierboxHistory', { _id: cashierBoxId }],
      })]);
    }
  });
};
